/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { CustomTextDocumentStore } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';

export type CustomTextVerificationDocumentStoreData = OmitFunctions<CustomTextVerificationDocumentStore>;
export type ExtractionLabelPredictionConflict = {
    label: CustomTextEntityLabelStore;
    prediction: CustomTextEntityLabelStore;
    isConflict: boolean;
};
export type ClassificationLabelPredictionConflict = { label: string; prediction: string; isConflict: boolean };
type LabelPredictionConflict = ExtractionLabelPredictionConflict | ClassificationLabelPredictionConflict;

export class CustomTextVerificationDocumentStore extends CustomTextDocumentStore {
    public classPredictions: string[];

    public entityPredictions: CustomTextEntityLabelStore[];

    public labelPredictionMap: Map<string, LabelPredictionConflict>;

    public classificationLabelPredictionObj: LabelPredictionConflict;

    constructor(documentData: Partial<CustomTextVerificationDocumentStoreData>) {
        super(documentData);
        this.classPredictions = documentData.classPredictions ?? [];
        this.entityPredictions = documentData.entityPredictions ?? [];

        this._mapLabelsAndPredictions();
    }

    /**
     * Checks if the document has conflicts or not
     */
    @computed
    public get hasConflicts() {
        return this.extractionConflicts.size > 0 || this.classificationLabelPredictionObj?.isConflict;
    }

    /**
     * Calculates the conflicts of the document's labels and predictions
     */
    @computed
    public get extractionConflicts() {
        const conflictsMap = new Map<string, LabelPredictionConflict>();
        this.labelPredictionMap.forEach((value, key) => {
            if (value.isConflict) {
                conflictsMap.set(key, <LabelPredictionConflict>{ label: value.label, prediction: value.prediction });
            }
        });

        return conflictsMap;
    }

    /**
     * Updates the document tokenized text, labels and predictions.
     */
    @action
    public initialize(text: string) {
        super.initialize(text);
    }

    /**
     * Maps the labels and predictions based on their char indices.
     */
    @action
    private _mapLabelsAndPredictions() {
        this.labelPredictionMap = new Map<string, LabelPredictionConflict>();

        if (this.entityLabels.length || this.entityPredictions.length) {
            this.entityPredictions.forEach(prediction => {
                const { startCharIndex } = prediction;
                const { endCharIndex } = prediction;
                const label = this.entityLabels.find(
                    entityLabel => entityLabel.startCharIndex === startCharIndex && entityLabel.endCharIndex === endCharIndex
                );
                const isConflict = !label || label.entityId !== prediction.entityId;

                this.labelPredictionMap.set(`${startCharIndex}-${endCharIndex}`, { label, prediction, isConflict });
            });

            this.entityLabels.forEach(label => {
                const { startCharIndex } = label;
                const { endCharIndex } = label;

                if (this.labelPredictionMap.has(`${startCharIndex}-${endCharIndex}`)) {
                    return;
                }

                const prediction = this.entityPredictions.find(
                    entityPrediction => entityPrediction.startCharIndex === startCharIndex && entityPrediction.endCharIndex === endCharIndex
                );

                const isConflict = !prediction || prediction.entityId !== label.entityId;

                this.labelPredictionMap.set(`${startCharIndex}-${endCharIndex}`, { label, prediction, isConflict });
            });
        }

        if (this.classLabels.length || this.classPredictions.length) {
            const clsLabels = this.classLabels
                .map(c => c.name)
                .slice()
                .sort()
                .join(', ');
            const clsPredictions = this.classPredictions
                .slice()
                .sort()
                .join(', ');
            const isConflict = clsLabels !== clsPredictions;

            this.classificationLabelPredictionObj = { label: clsLabels, prediction: clsPredictions, isConflict };
        }
    }
}
